import { useContext, useEffect, useState } from "react"
import { connect, useDispatch, useSelector } from "react-redux"
import { useLazyQuery, gql } from "@apollo/client"
import { get, isEmpty, noop, toLower } from "lodash"
import { IntlProvider } from "react-intl"
import { useLocation } from "react-router"
import PropTypes from "prop-types"
import "moment/locale/de"
import "moment/locale/en-gb"
import "moment/locale/es"
import "moment/locale/fr"
import "moment/locale/hu"
import "moment/locale/pt"
import "moment/locale/cs"
import "moment/locale/sk"
import "moment/locale/sl"
import "moment/locale/it"
import "moment/locale/pl"
import defaultMessages from "../../i18n/json/en.json"
import formats from "../../i18n/formats"
import { fetchAccessibleLicenceModules } from "../../actions/accessibleLicenceModules"
import { fetchUserRights } from "../../actions/userRights"
import { fetchAmountUnits } from "../../actions/planningObjects/amountUnits"
import { fetchResources } from "../../actions/basedata/resources"
import { fetchCustomers } from "../../actions/basedata/customers"
import { fetchLanguages } from "../../actions/pages/languages"
import { createAction } from "~/utils/redux"
import { get as getHttp } from "~/utils/http"
import ActionTypes from "~/enums/ActionTypes"
import { API_SERVER_USER } from "~/constants/api"
import { useInit } from "../App/useInit"
import useFetchSettings from "~/hooks/useFetchSettings"
import LocalizationProviderDatePicker from "./LocalizationProviderDatePicker"
import { isScreenPage } from "~/utils/checkLocation"
import { getUserToken } from "~/selectors/token"
import { DateSettingsContext } from "./DateSettingsProvider"

const ADDITIONAL_PROPS = gql`
  query propertyDefinitions {
    properties {
      items {
        id
        datatype
        propertyName
        propertyType
      }
    }
  }
`
const mapDispatchToProps = {
  fetchAccessibleLicenceModules,
  fetchAmountUnits,
  fetchCustomers,
  fetchLanguages,
  fetchResources,
  fetchUserRights,
}
const Empty = () => <span />

const userLanguageEndpoint = "/users/translation"
const clientLanguageEndpoint = "/clients/translation"
const screenLanguageEndpoint = "/screens/translation"
const fallbackLanguage = "de"

const getLanguageEndpoint = (authenticated, pinAuthenticated, isTVScreen, location) => {
  if (isTVScreen) {
    const screenIdentifier = location.pathname.split("/").pop()
    return `${screenLanguageEndpoint}/${screenIdentifier}`
  }
  if (pinAuthenticated || authenticated) {
    return userLanguageEndpoint
  }
  return clientLanguageEndpoint
}

const useProperties = () => {
  const dispatch = useDispatch()
  const [getProperties] = useLazyQuery(ADDITIONAL_PROPS, {
    onCompleted: data => {
      const props = get(data, "properties.items", [])
      dispatch(createAction(ActionTypes.ADDITIONAL_PROPERTIES_SUCCESS, props))
    },
  })
  return getProperties
}

const useLanguageFromStore = () => {
  const screenLanguage = useSelector(state => get(state, "user.screen.screenLanguage"))
  const userLanguage = useSelector(state => get(state, "user.authentication.user.language"))
  return screenLanguage || userLanguage ? toLower(screenLanguage || userLanguage) : null
}

const loadLocalTranslations = (lang, setData) => {
  import(/* webpackChunkName: "translations[index]" */ `../../i18n/json/${lang}.json`)
    .then(module => {
      setData(module.default, lang)
    })
    .catch(() =>
      import(/* webpackChunkName: "translations[index]" */ `../../i18n/json/${fallbackLanguage}.json`).then(module => {
        setData(module.default, fallbackLanguage)
      })
    )
}

const InitializeApp = ({
  children,
  textComponent,
  fetchAccessibleLicenceModules: fetchAccessibleLicenceModulesAction,
  fetchAmountUnits: fetchAmountUnitsAction,
  fetchCustomers: fetchCustomersAction,
  fetchLanguages: fetchLanguagesAction,
  fetchResources: fetchResourcesAction,
  fetchUserRights: fetchUserRightsAction,
}) => {
  const fetchSettings = useFetchSettings()
  const location = useLocation()
  const isTVScreen = isScreenPage(location)
  const getProperties = useProperties()
  const token = useSelector(state => getUserToken(state))
  const authenticated = useSelector(state => get(state, "user.authentication.authenticated"))
  const pinAuthenticated = useSelector(state => get(state, "user.terminalAuthentication.pinAuthenticated"))
  const languageFromStore = useLanguageFromStore()
  const { locale: dateLocale, isHour12 } = useContext(DateSettingsContext)
  const [language, setLanguage] = useState()
  const [localeMessages, setLocaleMessages] = useState({})
  const [backendPending, setBackendPending] = useState(null)
  const [salesRecover, setSalesRecover] = useState(null)

  useInit(location, authenticated, backendPending, salesRecover)
  useEffect(() => {
    if (authenticated) {
      fetchLanguagesAction()
      fetchAccessibleLicenceModulesAction()
      fetchAmountUnitsAction()
      fetchCustomersAction()
      fetchResourcesAction()
      fetchUserRightsAction()
      fetchSettings()
      getProperties()
    }
  }, [authenticated])

  useEffect(() => {
    if (pinAuthenticated) {
      fetchSettings()
      fetchAccessibleLicenceModulesAction()
    }
  }, [pinAuthenticated])

  const setData = (translations, lang, isBackendStarting = false, isRecoveringSales = false) => {
    const mergedTranslations = { ...defaultMessages, ...translations }
    setBackendPending(isBackendStarting)
    setSalesRecover(isRecoveringSales)
    setLanguage(lang)
    setLocaleMessages(mergedTranslations)
  }

  useEffect(() => {
    const endpoint = getLanguageEndpoint(authenticated, pinAuthenticated, isTVScreen, location)
    if (!authenticated && !pinAuthenticated && !isTVScreen && language) {
      return
    }
    getHttp({
      url: `${API_SERVER_USER}${endpoint}`,
      token: authenticated || pinAuthenticated ? token : null,
    })
      .then(fetchData => {
        const receivedTranslations = get(fetchData, "translations")
        const receivedLanguage = get(fetchData, "language")
        const isBackendStarting = get(fetchData, "backendStarting")
        const isRecoveringSales = get(fetchData, "salesRecover")
        if (!isBackendStarting) {
          if (!receivedTranslations) {
            const lang = receivedLanguage || languageFromStore
            loadLocalTranslations(lang, setData)
            return
          }
        }
        setData(receivedTranslations, receivedLanguage, isBackendStarting, isRecoveringSales)
      })
      .catch(() => {
        loadLocalTranslations(languageFromStore, setData)
      })
  }, [authenticated, pinAuthenticated, languageFromStore])

  formats.date.hour12 = isHour12
  return (
    <IntlProvider
      formats={formats}
      key={language || "nix"}
      locale={dateLocale}
      messages={localeMessages}
      onError={isEmpty(localeMessages) ? noop : undefined}
      textComponent={isEmpty(localeMessages) ? Empty : textComponent}
    >
      <LocalizationProviderDatePicker locale={dateLocale}>{children}</LocalizationProviderDatePicker>
    </IntlProvider>
  )
}

InitializeApp.defaultProps = {
  textComponent: undefined,
}

InitializeApp.propTypes = {
  textComponent: PropTypes.symbol,
  fetchAccessibleLicenceModules: PropTypes.func.isRequired,
  fetchAmountUnits: PropTypes.func.isRequired,
  fetchCustomers: PropTypes.func.isRequired,
  fetchLanguages: PropTypes.func.isRequired,
  fetchResources: PropTypes.func.isRequired,
  fetchUserRights: PropTypes.func.isRequired,
}

export default connect(null, mapDispatchToProps)(InitializeApp)
